import {
  Box,
  Button,
  DrawerHeader,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ScaleFade,
  Stack,
  Text,
  Textarea,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import MessageError from '../../../../components/MessageError';
import { FormProvider, useForm } from 'react-hook-form';
import { ITitleTopics } from '../../../../services/hooks/Titles/useGetTitlesTopicsData';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSendTitle } from '../../../../services/hooks/Titles/useSendTitle';
import { FiCheck } from 'react-icons/fi';
import { IoMdReturnLeft } from 'react-icons/io';
import InputMobile from '../../../../components/Input/Mobile';
import { PiListNumbersLight } from 'react-icons/pi';
import InputAttachmentFile from '../../Resources/mobile/create/attachmentFile';
import { validateTypeFile, validationSizeFile } from '../../../../utils/validationForm';
import { ITitleTypeAndRulesProps } from '../../../../services/hooks/Titles/useGetTitleTypesAndRules';

type FormValues = {
  declaration_value?: string;
  observation?: string;
  attachmentFile: FileList;
};

interface FormTitleContentProps {
  topic: ITitleTopics;
  registrationId: string;
  stepId: string;
  refetchData: () => void;
  closeModal: () => void;
  rules: ITitleTypeAndRulesProps;
}

const unidadeOptions = [
  { value: '', label: 'Selecione' },
  { value: 'unity', label: 'Único' },
  { value: 'hour', label: 'horas' },
  { value: 'day', label: 'dias' },
  { value: 'week', label: 'semana' },
  { value: 'month', label: 'mês' },
  { value: 'year', label: 'anos' },
];

const getUnityLabel = (unity: string) => {
  const found = unidadeOptions.find((opt) => opt.value === unity);
  return found ? found.label : unity;
};

const FormTitleContent: React.FC<FormTitleContentProps> = ({
  topic,
  registrationId,
  stepId,
  refetchData,
  closeModal,
  rules,
}) => {
  const diclosureConfirmation = useDisclosure();
  const toast = useToast();
  const schema = yup.object().shape({
    declaration_value: yup.string().when({
      is: () => topic.unity !== 'unity',
      then: (schema) => schema.required('Valor declaração é obrigatório'),
    }),
    observation: yup.string(),
    attachmentFile: yup
      .mixed<FileList>()
      .required('O arquivo é obrigatório')
      .test('Required', 'O arquivo é obrigatório', (file) => {
        const listFile = file as FileList;

        let hasError = true;
        if (listFile.length == 0) hasError = false;

        return hasError;
      })
      .test('invalidExtension', 'Verifique o nome do arquivo enviado', (file) => {
        const listFile = file as FileList;
        let hasError = true;

        for (let index = 0; index < listFile?.length; index++) {
          const hasDot = listFile[index].name.split('.');
          if (hasDot.length > 2) hasError = false;
        }

        return hasError;
      })
      .test('validationSizeFile', `Tamanho máximo para arquivos é de ${rules.size_attached}MB`, (file) => {
        const listFile = file as FileList;

        if (listFile?.length > 0) {
          const response = validationSizeFile(listFile, rules.size_attached);
          // if (!response) {
          //   setIsOpen(true);
          // }
          return response;
        }

        return true;
      })
      .test('validationTypeFile', 'Formato de arquivo não suportado', (file) => {
        const listFile = file as FileList;
        if (listFile?.length > 0) {
          return validateTypeFile(listFile);
        }
        return true;
      }),
  });

  const hookForms = useForm<FormValues>({
    resolver: yupResolver(schema),
  });

  const requestSendTitle = useSendTitle();

  const fileList = hookForms.watch('attachmentFile');
  const file = fileList && fileList.length > 0 ? fileList[0] : null;

  const sendTitle = (data: FormValues) => {
    console.log(data);
    const fileToSend = data.attachmentFile[0];

    if (fileToSend) {
      requestSendTitle
        .mutateAsync({
          payload: data,
          registrationId: registrationId,
          stepId: stepId,
          file: fileToSend,
          topicId: topic.id,
        })
        .then(() => {
          diclosureConfirmation.onOpen();
          // refetchData();
        })
        .catch((error) => {
          toast({
            title: 'Atenção',
            description: error?.response?.data?.message || 'Erro na solicitação',
            status: 'warning',
            duration: 4000,
            isClosable: true,
          });
        });
    }
  };

  const finishForm = () => {
    refetchData();
    hookForms.reset();
    closeModal();
    diclosureConfirmation.onClose();
  };

  const accent = '#222';

  return (
    <>
      <DrawerHeader>
        <Text fontSize={'medium'} textAlign={'center'}>
          Envio de arquivo
        </Text>
        <Text fontSize={'medium'} textAlign={'center'} fontWeight={'normal'}>
          Siga os passos abaixo com atenção para enviar um arquivo para o tópico <strong>{topic.name}</strong>
        </Text>
        <Text fontSize={'medium'} textAlign={'center'} fontWeight={'normal'}>
          Após preencher todos os campos, clique no botão <strong>Enviar</strong> Localizado ao final desta página.
        </Text>
        <Flex onClick={finishForm} justify={'center'} cursor={'pointer'} mt={4} color={'blue.300'} gap={1}>
          <IoMdReturnLeft />
          <Text>Voltar</Text>
        </Flex>
      </DrawerHeader>
      <FormProvider {...hookForms}>
        <form style={{ width: '100%' }} onSubmit={hookForms.handleSubmit(sendTitle)}>
          <Flex gap={5} flexDir={['column', 'column', 'row', 'row', 'row']}>
            <Box borderRadius={'md'} shadow={'md'} minH={'100%'} w={'100%'}>
              <Box color={'white'} borderRadius={'md'} p={2} bg={'gray'}>
                <Text fontSize={'md'} fontWeight={'bold'}>
                  Formulário
                </Text>
              </Box>
              <Stack p={5}>
                <Box mt={2}>
                  {/* <Text mb={1}>Observações</Text> */}
                  {topic.unity !== 'unity' && (
                    <InputMobile
                      type="textarea"
                      name="declaration_value"
                      placeholderLabel={`Informe a quantidade de ${getUnityLabel(topic.unity)}:`}
                      iconLeft={<PiListNumbersLight size={16} color={accent} />}
                      isRequired
                    />
                    // <FormControl isRequired w={['100%', '100%', '100%', '50%', '50%']}>
                    //   <FormLabel>Informe a quantidade de {getUnityLabel(topic.unity)}:</FormLabel>

                    //   <Input isRequired={false} {...hookForms.register('declaration_value')} />
                    //   <MessageError>{hookForms?.formState?.errors?.declaration_value?.message}</MessageError>
                    // </FormControl>
                  )}

                  <FormControl mt={2}>
                    <FormLabel fontSize={'14px'}>Observações:</FormLabel>
                    <Textarea
                      style={{
                        width: '100%',
                        minHeight: '80px',
                        borderRadius: '8px',
                        border: '1px solid #CBD5E0',
                        padding: '8px',
                        resize: 'vertical',
                      }}
                      {...hookForms.register('observation')}
                      placeholder="Digite suas observações aqui..."
                    />
                    <MessageError>{hookForms?.formState?.errors?.observation?.message}</MessageError>
                  </FormControl>
                </Box>
                <Box>
                  <FormControl>
                    <FormLabel fontSize={'14px'}>Arquivo</FormLabel>
                    <InputAttachmentFile allowMultiple={false} />
                    {/* <Input
                      alignContent={'center'}
                      type="file"
                      accept="image/*,application/pdf"
                      {...hookForms.register('file')}
                    /> */}
                    {/* <MessageError>{hookForms?.formState?.errors?.attachmentFile?.message}</MessageError> */}
                  </FormControl>
                </Box>
                <Flex align={'flex-end'} gap={4} justify={'flex-end'} alignContent={'center'}>
                  {!requestSendTitle.isPending && (
                    <Text
                      fontSize={'medium'}
                      color={'colorLink'}
                      cursor={'pointer'}
                      onClick={() => {
                        finishForm();
                      }}
                    >
                      Cancelar
                    </Text>
                  )}

                  <Button isLoading={requestSendTitle.isPending} type="submit" colorScheme="green" mt={4}>
                    ENVIAR
                  </Button>
                </Flex>
              </Stack>
            </Box>

            {file && (
              <Box shadow={'md'} borderRadius={'md'} w={'100%'}>
                <Box borderRadius={'md'} p={2} bg={'gray'}>
                  <Text color={'white'} fontSize={'md'} fontWeight={'bold'}>
                    Pré-visualização
                  </Text>
                </Box>
                <Box minH={'600px'} mt={2}>
                  {file ? (
                    file.type.startsWith('image/') ? (
                      <img
                        src={URL.createObjectURL(file)}
                        alt="Preview"
                        style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }}
                      />
                    ) : file.type === 'application/pdf' ? (
                      <iframe
                        src={URL.createObjectURL(file)}
                        title="PDF Preview"
                        style={{ width: '100%', height: '600px', borderRadius: '8px', border: '1px solid #CBD5E0' }}
                      />
                    ) : (
                      <Text>{file.name}</Text>
                    )
                  ) : (
                    <Flex align={'center'} justify={'center'}>
                      <Text mt={10} fontSize={'medium'}>
                        Nenhum arquivo selecionado
                      </Text>
                    </Flex>
                  )}
                </Box>
              </Box>
            )}
          </Flex>
        </form>
      </FormProvider>
      <Modal
        isOpen={diclosureConfirmation.isOpen}
        onClose={() => {
          finishForm();
        }}
        isCentered
        motionPreset="scale"
      >
        <ModalOverlay backdropFilter="blur(2px)" />
        <ScaleFade initialScale={0.9} in={diclosureConfirmation.isOpen}>
          <ModalContent maxW={['90%', '400px']} py={6} px={4} borderRadius="2xl" boxShadow="lg">
            <ModalHeader textAlign="center" p={0}>
              <HStack justify="center">
                <Box
                  bg="green.100"
                  borderRadius="full"
                  p={3}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon as={FiCheck} boxSize={8} color="green.600" />
                </Box>
              </HStack>
            </ModalHeader>

            <ModalBody textAlign="center" mt={4}>
              <Text fontSize="xl" fontWeight="bold" color="gray.800">
                Sucesso!
              </Text>
              <Text fontSize="md" color="gray.600" mt={2}>
                {'Seu envio foi concluído com sucesso.'}
              </Text>

              <Button
                mt={6}
                colorScheme="green"
                size="lg"
                borderRadius="full"
                w="full"
                onClick={() => {
                  finishForm();
                }}
              >
                Fechar
              </Button>
            </ModalBody>
          </ModalContent>
        </ScaleFade>
      </Modal>
    </>
  );
};

export default FormTitleContent;
